import React, { useEffect } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import TouchBox from 'Components/Capacitor/TouchBox';
import ReactPlayer from 'react-player';
import { OnProgressProps } from 'react-player/base';
import { IRefPhaserGame, PhaserGame } from 'Phaser/PhaserGame';
import { replaceSceneNow } from 'Phaser/replaceSceneNow';
import { restartGameBoot } from 'Phaser/restartGameBoot';
import {
  keepsPauseOverlayAfterMidiPrompt,
  makeTutorialBackgroundPause,
  type TutorialBackgroundPause,
} from './backgroundPause';
import { SceneLoadGate } from 'Utils/SceneLoadGate';
import {
  PhaserToReactListeners,
  ReactToPhaserListeners,
  NotifyPhaser,
  ReactListenForPhaser,
  NotifyPhaserEvents,
  RemoveListener,
} from 'Phaser/EventBus2';
import ChapterList, { getTime, Timestamp } from '../Chapters';
import './style.css';
import Button from '@mui/material/Button';
import FastForwardRounded from '@mui/icons-material/FastForwardRounded';
import Fullscreen from 'Components/Fullscreen';
import { markChapterAsCompleted, syncChapterComplete, setLastChapter } from 'Actions/tutorial';
import {
  tutorialChapterCompletedEventAction,
  tutorialChapterStartedEventAction,
  tutorialCompletedEventAction,
} from 'Actions/events';
import { useDispatch, useSelector } from 'react-redux';
import { setLevelSelect } from 'Actions/app';
import { useNavigate } from 'react-router-dom';
import Play from '../../../assets/images/Play.png';
import { ChapterData, ChapterType, ExerciseScene, InteractiveEvent } from 'Types/ExerciseData';
import { useMediaEventCues } from 'Hooks/useMediaEventCues';
import { useVideoNarration } from 'Hooks/useVideoNarration';
import { useNarrationGain } from 'Volume/useNarrationGain';
import {
  applyVideoNarrationGain,
  narrationVideoCrossOrigin,
  videoVolumeIgnored,
} from 'Volume/videoNarrationGain';
import VolumeMenuButton from 'Volume/VolumeMenuButton';
import { volumeControlsAvailable } from 'Volume/volumeAvailability';
import {
  buildEventSchedule,
  chapterAfterExercise,
  chapterOfActiveExercise,
  keysHandledBefore,
  selectDueEvents,
  takeThroughFirstPause,
  type ScheduledEvent,
} from 'Utils/VideoEventSchedule';
import { setPlaybackSpeedState } from 'Actions/tutorial';
import { completesTutorial } from 'Utils/tutorialCompletion';
import * as Immutable from 'immutable';
import { CustomCircularProgress } from 'Components/StyledComponents';
import { useMidiContext } from 'Contexts/MidiContext';
import { isCapacitorNative } from 'Utils/Constants';
import { containedPictureSize, exactSizeOf } from './videoPicture';
import {
  chapterNeedsAny,
  chapterPhrasesIn,
  chapterPhrasesPending,
  downloadTutorialPhrases,
  PHRASE_DOWNLOAD_RETRY_DELAYS_MS,
  withDownloadedPhrases,
  type TutorialPhrases,
} from './tutorialPhraseDownloads';
import { MainAppReducer, TutorialData2, LevelStatus, ObjectiveData } from 'Types';
import * as lessonActions from 'Actions/lesson';
import logger from 'Utils/Logger';
import * as Tone from 'tone';
import LessonDidNotLoad from 'Components/LessonDidNotLoad';
import { useStalledFor } from 'Hooks/useStalledFor';
import { useDidNotLoadMessageAllowed } from 'Hooks/useDidNotLoadMessageAllowed';
import { RootState } from 'Reducers';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import Logger from 'Utils/Logger';
import { useEffectiveGrading } from 'Utils/playAlong/effectiveGrading';
import PlayAlongIdlePause from 'Components/PlayAlong/PlayAlongIdlePause';
import type { TutorialInputUsed } from 'Types/EventTypes';
import TutorialNextButton from './TutorialNextButton';
import { useTapInput } from './useTapInput';
import TapInputPauseButton from './TapInputPauseButton';
import { useReturnFocusAfterControlUse } from './useReturnFocusAfterControlUse';
import { sceneTakesTaps } from 'Utils/playAlong/tapInput';
import type { NextRing } from './useNextRing';

type Visibility = 'hidden' | 'visible';

/** MUS-651: how long a tutorial step may sit with nothing to watch or play. */
export const TUTORIAL_STEP_TIMEOUT_MS = 20000;

const VIDEO_SRC_PROD = 'https://assets.museflow.ai/videos/interactive_tutorials/';

/*const TUTORIAL_VID_URL_PREFIX =
  process.env.REACT_APP_NODE_ENV === 'local'
    ? 'http://localhost:8000/'
    : VIDEO_SRC_PROD;*/
const TUTORIAL_VID_URL_PREFIX = VIDEO_SRC_PROD;

const SkipButton = ({ skip, visibility }: { skip: () => void; visibility?: Visibility }) => {
  return (
    <Button
      id="skip-button"
      className={visibility}
      sx={{
        backgroundColor: '#4A5AA1',
        borderRadius: '35px',
        width: '5%',
        minWidth: 'unset',
        height: 'calc(5% * 1.777778)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'absolute',
        bottom: '10%',
        opacity: 0,
        right: '10%',
        padding: 'none',
        pointerEvents: 'all',
      }}
      variant="contained"
      onClick={() => {
        skip();
      }}
      onKeyUp={(e) => {
        if (e.key === ' ') e.preventDefault();
      }}
    >
      <FastForwardRounded sx={{ height: '100%', width: 'unset' }} />
    </Button>
  );
};

const ProceedButton = ({
  proceed,
  visibility,
}: {
  proceed: () => void;
  visibility: Visibility;
}) => {
  return (
    <TouchBox onClick={proceed} id="proceed-button">
      <Box sx={playButtonStyles} className={visibility}>
        <img
          alt="play_button"
          style={{ maxWidth: '100%', maxHeight: '100%', margin: '0px', borderRadius: '100%' }}
          src={Play}
        />
      </Box>
    </TouchBox>
  );
};

const playButtonStyles = {
  position: 'absolute',
  width: '70px',
  height: '70px',
  top: 'calc(58%)',
  left: 'calc(50% - 35px)',
  padding: '0px',
  margin: '0px',
  borderRadius: '50%',
  pointerEvents: 'all',
  '&:hover': {
    background: 'rgba(212,134,108,1.0)',
    cursor: 'pointer',
    transition: '0.3s',
    boxShadow: '0px 0px 5px 5px rgb(212,134,108,.5)',
  },
};

const mobileQuery = '(max-width: 768px), (max-height: 500px)';

/** Marks a tap for the scene (tap input); other notes go as before. */
const tapMark = (ev: { tap?: boolean }): { tap?: boolean } => (ev.tap ? { tap: true } : {});

/** What an exercise was played on, when its scene did not say: MIDI when graded. */
const inputHeardWith = (sceneIsGraded: boolean): TutorialInputUsed =>
  sceneIsGraded ? 'midi' : 'none';

/**
 * How far past an event's timestamp the playhead may sit before a pause
 * rewinds to it (MUS-184). Small overshoots are normal and rewinding them
 * would cause a visible stutter; large ones mean the notification was
 * delayed by main-thread contention and the video ran into the exercise.
 */
const MAX_PAUSE_OVERSHOOT_SECONDS = 0.15;

/** Progress polling cadence. Drives chapter highlighting and the event backstop. */
const PROGRESS_INTERVAL_MS = 200;

/**
 * How the pause overlay darkens the video and the exercise: the same as a
 * #000000aa layer over them. It is drawn on the video and the game themselves,
 * not by a dark box laid over them: the browser places a video's picture on
 * the screen's pixels by its own rounding, and a box of the same size was
 * still a pixel or two short of it at some edges, leaving a bright sliver.
 */
const PAUSED_DIM_FILTER = 'brightness(0.333)';

/** The video's own aspect ratio once it is known, else the one the tutorials are made in. */
const pictureRatioOf = (video: HTMLVideoElement, fallback: number): number =>
  video.videoWidth > 0 && video.videoHeight > 0 ? video.videoWidth / video.videoHeight : fallback;

/** The <video> element behind the player, once it has one. */
const videoElementOf = (player: ReactPlayer | null): HTMLVideoElement | undefined => {
  const element = player?.getInternalPlayer();
  return element instanceof HTMLVideoElement ? element : undefined;
};

// These are the layouts that will be rendered!!!
const InteractiveTemplate1 = ({
  tutorialData,
  chaptersVisible,
}: {
  tutorialData: TutorialData2;
  chaptersVisible: boolean;
}) => {
  const isMobile = useMediaQuery(mobileQuery);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { checkMidiAndShowModal, tapNoteOn, tapNoteOff } = useMidiContext();

  // Whether the app can hear the player. An exercise is loaded graded or not
  // and keeps that until the next one loads (sceneGraded); without a heard
  // input it plays itself and its Skip button reads "Next".
  const graded = useEffectiveGrading();
  const gradedRef = React.useRef(graded);
  gradedRef.current = graded;
  const [sceneGraded, setSceneGraded] = React.useState(true);
  const sceneGradedRef = React.useRef(true);
  // The exercise scene loaded now: the exercises played with any key take taps.
  const [loadedSceneName, setLoadedSceneName] = React.useState<string>();
  const loadedSceneGrading = React.useCallback((sceneIsGraded: boolean, sceneName: string) => {
    sceneGradedRef.current = sceneIsGraded;
    setSceneGraded(sceneIsGraded);
    setLoadedSceneName(sceneName);
  }, []);
  // Ungraded: the ring closing around Next, while it runs.
  const [nextRing, setNextRing] = React.useState<NextRing | null>(null);
  const nextRingCountRef = React.useRef(0);

  const [chapterData, setChapterData] = React.useState<ChapterData[]>();

  React.useEffect(() => {
    const chapters = tutorialData.tutorial_config.sections.flatMap((x) => x.chapters);
    setChapterData(chapters);
    logger.debug('chapters:', chapters);
  }, [tutorialData]);

  /**
   * MUS-184: every exercise event in the tutorial, flattened and ordered by
   * timestamp. Events fire off this schedule rather than by scanning the
   * current chapter, so an event is always attributed to the chapter that
   * actually declares it.
   */
  const eventSchedule = React.useMemo(
    () => (chapterData ? buildEventSchedule(chapterData) : []),
    [chapterData],
  );

  /** Keys of events already dispatched; the de-duplication gate for both paths. */
  const firedEventsRef = React.useRef<Set<string>>(new Set());

  /** The underlying media element, used for cue registration and exact pausing. */
  const [videoElement, setVideoElement] = React.useState<HTMLVideoElement | null>(null);
  const videoElementRef = React.useRef<HTMLVideoElement | null>(null);
  React.useEffect(() => {
    videoElementRef.current = videoElement;
  }, [videoElement]);

  type TimestampInfo = {
    load?: Timestamp;
    start?: Timestamp;
  };

  /*
   * timestamps: [chapter][objective] = {
   *   load: timestamp,
   *   start: timestamp,
   *   ...
   * }
   */
  const [timestamps, setTimestamps] = React.useState<TimestampInfo[][]>([]);

  const [phrases, setPhrases] = React.useState<TutorialPhrases>([]);
  // MUS-651: objectives whose phrase did not download, and those being tried
  // again after the step's Retry. Their chapters wait; the others play on.
  const [phrasesFailed, setPhrasesFailed] = React.useState<ObjectiveData[]>([]);
  const [phrasesRetrying, setPhrasesRetrying] = React.useState<ObjectiveData[]>([]);
  const isUnmountedRef = React.useRef(false);
  React.useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);
  /** Download these objectives' phrases, each on its own, and keep the ones that arrive. */
  const downloadPhrases = React.useCallback(
    async (objectives: readonly ObjectiveData[], retryDelaysMs: readonly number[]) => {
      const failed = await downloadTutorialPhrases(
        objectives,
        retryDelaysMs,
        () => isUnmountedRef.current,
        (downloaded) => setPhrases((previous) => withDownloadedPhrases(previous, [downloaded])),
      );
      if (isUnmountedRef.current) return;
      failed.forEach((objective) =>
        console.error(
          `[MUS-651] tutorial phrase did not download: chapter ${objective.chapter} objective ${objective.objective}`,
        ),
      );
      setPhrasesFailed((previous) => [
        ...previous.filter((objective) => !objectives.includes(objective)),
        ...failed,
      ]);
    },
    [],
  );
  const [phaserReady, setPhaserReady] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!chapterData) return;
    const _timestamps: TimestampInfo[][] = [];
    Logger.debug('timestamps chapter data: ', chapterData);
    chapterData.forEach((chapter, chapterIdx) => {
      const [chapterType, chapterProps] = chapter.data;
      if (chapterType === ChapterType.Exercise) {
        _timestamps[chapterIdx] = [];
        let objectiveIdx = -1;
        chapterProps.events.forEach(({ at, phaserEvent }: InteractiveEvent) => {
          const [rawEventType] = phaserEvent;
          switch (rawEventType) {
            case 'UpdateObjective': {
              objectiveIdx++;
              _timestamps[chapterIdx][objectiveIdx] = {
                ..._timestamps[chapterIdx][objectiveIdx],
                load: at,
              };
              break;
            }
            case 'StartExercise': {
              _timestamps[chapterIdx][objectiveIdx] = {
                ..._timestamps[chapterIdx][objectiveIdx],
                start: at,
              };
              break;
            }
            default: {
              void 0; // intentionally empty
            }
          }
        });
      }
    });
    Logger.debug('timestamps: ', _timestamps);
    setTimestamps(_timestamps);
    void downloadPhrases(tutorialData.objective_data, PHRASE_DOWNLOAD_RETRY_DELAYS_MS);
  }, [tutorialData, chapterData, downloadPhrases]);

  const phaserRef = React.useRef<IRefPhaserGame | null>(null);
  const videoRef = React.useRef<ReactPlayer>(null);
  const isNavigatingRef = React.useRef<boolean>(false);

  const [exercisePaused, setExercisePaused] = React.useState(true);
  const [showExercise, setShowExercise] = React.useState(false);
  const [_isExercise, setIsExercise] = React.useState(false);
  const { hasInteracted, cbType } = useSelector((state: MainAppReducer) => ({
    hasInteracted: state.mainAppReducer.interactedWithPage,
    cbType: state.mainAppReducer.userData?.colorblind_type,
  }));
  const userSubscriptionStatus = useSelector(
    (state: MainAppReducer) => state.mainAppReducer.userData?.subscription_status,
  );
  const { showMidiModal, dismissedMidiModal } = useMidiContext();
  const narrationGain = useNarrationGain();
  // The `volume` prop covers every platform but iOS, which ignores it.
  React.useEffect(() => {
    if (videoElement) applyVideoNarrationGain(videoElement, narrationGain);
  }, [videoElement, narrationGain]);
  const videoPlayerConfig = React.useMemo(
    () => ({ file: { attributes: { crossOrigin: narrationVideoCrossOrigin() } } }),
    [],
  );
  const lastChapter = useSelector((state: RootState) => {
    return Immutable.get(state.tutorialReducer?.lastChapter, tutorialData.level_number);
  });

  // Get the set of completed chapters for this level
  const completedChapters = useSelector((state: RootState) => {
    return Immutable.get(state.tutorialReducer?.completed, tutorialData.level_number) as
      | Immutable.Set<number>
      | undefined;
  });

  // Existing progress for this level, if any. Never-played levels have no entry
  // here, which is what drives the grey roadmap node. We use this to decide
  // whether initiating the tutorial should mark the level as "started".
  const currentLevelULP = useSelector((state: RootState) =>
    state.mainAppReducer.userLevelData.find(
      (ulp) => ulp.level?.level_number === tutorialData.level_number,
    ),
  );

  const [currentChapter, setCurrentChapter] = React.useState<number | undefined>(
    lastChapter as number,
  );
  // || (currentChapter && currentChapter < 1 && autoplay);
  // const manualPause = useSelector((state: RootState) => state.tutorialReducer?.manualPause ?? false);
  const autoplay =
    (hasInteracted && !showMidiModal) ||
    (currentChapter !== undefined && hasInteracted && currentChapter < 1);
  const [manualPause, setManualPause] = React.useState<boolean>(!autoplay);
  const [isVideoPlaying, setIsVideoPlaying] = React.useState<boolean>(autoplay);
  // Mirror of manualPause state for use inside the [] -dep visibility effect,
  // which otherwise captures a stale closure of the state value.
  const manualPauseRef = React.useRef(manualPause);
  React.useEffect(() => {
    manualPauseRef.current = manualPause;
  }, [manualPause]);
  // Same, for whether an exercise is on screen: one that is stays paused when
  // the app comes back from the background (MUS-625).
  const showExerciseRef = React.useRef(showExercise);
  showExerciseRef.current = showExercise;
  // MUS-651: the phrases of the exercise chapter on screen are still on their
  // way. The video waits for them, so the chapter's scene is asked for before
  // the video can reach its first objective (a scene started after that never
  // hears it, and its exercise never appears). A phrase that failed lets the
  // video go on to where the exercise would start, and the step says so there.
  const videoWaitsForPhrases =
    !showExercise &&
    chapterPhrasesPending(phrases, tutorialData.objective_data, phrasesFailed, currentChapter ?? 0);
  // MUS-651: the exercise chapter the tutorial opened at, until its scene has
  // been asked for. Its first objective can come a fraction of a second into
  // the chapter, and the scene is asked for half a second after the chapter is
  // entered (and only once the game is ready and the phrases are in): a video
  // that started on its own when it loaded reached that objective first, the
  // scene never heard it, and the exercise never appeared. A chapter jump has
  // its own hold (SceneLoadGate). A phrase that failed lets the video go on to
  // where the exercise would start, and the step says so there.
  const [chapterOpenedAwaitingScene, setChapterOpenedAwaitingScene] = React.useState<number>();
  const videoWaitsForScene =
    chapterOpenedAwaitingScene !== undefined &&
    !chapterNeedsAny(phrasesFailed, chapterOpenedAwaitingScene);
  const videoHeld = videoWaitsForPhrases || videoWaitsForScene;
  // const dispatchManualPause = React.useCallback((val: boolean) => dispatch(setManualPause({ manualPause: val })), [dispatch]);
  const playbackSpeed = useSelector((state: RootState) => {
    return state.tutorialReducer?.playbackSpeed ?? 1;
  });

  const prevShowMidiModalRef = React.useRef(showMidiModal);
  // MUS-651: whether the video was playing when the prompt stopped it.
  const videoPlayingBeforeMidiPromptRef = React.useRef<boolean | undefined>(undefined);
  useEffect(() => {
    Logger.log('midiNotFound', showMidiModal);
    if (
      showMidiModal &&
      currentChapter !== undefined &&
      currentChapter > 0 &&
      !dismissedMidiModal
    ) {
      console.log('[EXERCISE-DBG] setIsVideoPlaying(false) + setManualPause(true) from MIDI modal');
      if (videoPlayingBeforeMidiPromptRef.current === undefined) {
        videoPlayingBeforeMidiPromptRef.current = isVideoPlaying;
      }
      setIsVideoPlaying(false);
      setManualPause(true);
    } else if (prevShowMidiModalRef.current && !showMidiModal) {
      // MIDI modal was just dismissed (device connected or user closed it).
      // Only resume video if no exercise is currently active. If an exercise
      // is showing, the video would play through timed events (UpdateObjective,
      // StartExercise) that advance the exercise before the user completes the
      // current step. The video will resume via ObjectiveComplete instead.
      // MUS-626: an exercise that is still held paused keeps its overlay.
      const videoWasPlaying = videoPlayingBeforeMidiPromptRef.current === true;
      videoPlayingBeforeMidiPromptRef.current = undefined;
      const keepsOverlay = keepsPauseOverlayAfterMidiPrompt({
        exerciseShowing: showExercise,
        exercisePaused,
      });
      if (!keepsOverlay) {
        setManualPause(false);
        // MUS-651: the overlay comes down, so the scene must not stay paused
        // under it (one reloaded after a rebuilt game is held paused). Only
        // with no exercise showing: the prompt does not pause a running
        // exercise, and resuming one restarts its playback.
        if (!showExercise) NotifyPhaserEvents.ResumeExercise();
      }
      // MUS-651: an exercise that appeared while the video was still playing
      // on to its StartExercise needs the video to reach it, or it never
      // starts. Under a kept overlay the video stays held until the tap.
      if (!showExercise || videoWasPlaying) {
        setIsVideoPlaying(true);
        // MUS-651: a prompt that only flashed up stopped the element a moment
        // ago, and the player skips its own start while it still counts the
        // video as playing. Start the element here unless the overlay holds it.
        if (!keepsOverlay && phaserReady && !videoHeld) {
          const element = videoElementOf(videoRef.current);
          if (element?.paused) element.play().catch(() => undefined);
        }
      }
    }
    prevShowMidiModalRef.current = showMidiModal;
  }, [hasInteracted, showMidiModal, currentChapter, dismissedMidiModal]);

  const changePlaybackSpeed = React.useCallback(() => {
    dispatch(setPlaybackSpeedState(playbackSpeed >= 2.0 ? 0.5 : playbackSpeed + 0.25));
  }, [playbackSpeed, dispatch]);
  const [skipButtonVisibility, setSkipButtonVisibility] = React.useState<Visibility | undefined>(
    undefined,
  );
  const [proceedButtonVisibility, setProceedButtonVisibility] =
    React.useState<Visibility>('hidden');
  // Mirror for the background pause, set up once in a [] -dep effect.
  const proceedButtonVisibilityRef = React.useRef(proceedButtonVisibility);
  proceedButtonVisibilityRef.current = proceedButtonVisibility;

  // Track if level 0 completion has been fired to prevent duplicate dispatches
  const level0CompletionFiredRef = React.useRef(false);

  // Track if we've already marked this level as "started" so initiating the
  // first chapter only flips a never-played (grey) node to a green ring once.
  const levelStartedFiredRef = React.useRef(false);

  React.useEffect(() => {
    NotifyPhaserEvents.ChangePlaybackRate({ rate: playbackSpeed });
  }, [playbackSpeed]);

  const phaserLoadTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const checkChapterTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isManualSeekingRef = React.useRef(false);
  /** Tracks the chapter currently loaded in the Phaser scene to prevent
   *  redundant scene restarts when CheckChapter fires for the same chapter. */
  const activeSceneChapterRef = React.useRef<number | undefined>(undefined);
  /** True once the game is ready for scene loading (boot complete or singleton reused). */
  const gameReadyRef = React.useRef(false);
  /** MUS-651: a scene asked for while the game was still booting. */
  const sceneLoadWaitingForGameRef = React.useRef<{
    name: string;
    props: Record<string, unknown>;
  } | null>(null);

  const loadScene = React.useCallback(
    (name: string, props: Record<string, unknown>) => {
      const scene = phaserRef.current?.scene;
      const game = phaserRef.current?.game;
      const sceneIsActive = scene?.sys?.isActive() || scene?.sys?.isPaused();
      if (!gameReadyRef.current) {
        // Boot sequence hasn't completed yet. PhaserReady starts the scene
        // (see handlePhaserReady); the retry is a fallback.
        sceneLoadWaitingForGameRef.current = { name, props };
        if (phaserLoadTimer.current) clearTimeout(phaserLoadTimer.current);
        phaserLoadTimer.current = setTimeout(() => loadScene(name, props), 500);
        return;
      }

      sceneLoadWaitingForGameRef.current = null;
      if (phaserLoadTimer.current) clearTimeout(phaserLoadTimer.current);

      if (game) {
        // Use the game's SceneManager directly, never the on-screen scene's
        // ScenePlugin, which only queues the switch until the next game step.
        // MUS-651: the video resumes once the scene has been asked for, so a
        // late step let it reach the chapter's first UpdateObjective before
        // the scene was listening, and the exercise never loaded.
        // MUS-576: the scene may have asked to stop itself a moment ago (an
        // exercise does, 500 ms after its last objective). That stop is still
        // queued until the next game step, and left there it would shut down
        // the run started here. replaceSceneNow drops it, then stops and
        // starts the scenes in this call.
        replaceSceneNow(game.scene, sceneIsActive ? scene : null, name, props);
      } else {
        // Wait for phaser to initialise and try again
        phaserLoadTimer.current = setTimeout(() => loadScene(name, props), 500);
      }
    },
    [phaserRef],
  );

  /** MUS-576: holds the video after a chapter jump until the chapter's scene
   *  has been asked for (see jumpTo). */
  const sceneLoadGateRef = React.useRef(new SceneLoadGate());

  const onSeek = React.useCallback(() => {
    if (isManualSeekingRef.current) {
      isManualSeekingRef.current = false;
      // After a chapter jump the scene is loaded on the next animation frame.
      // If the seek got here first, that frame resumes the video instead.
      if (sceneLoadGateRef.current.seekCompleted()) setIsVideoPlaying(true);
    }
  }, []);

  const retryCountRef = React.useRef<Map<number, number>>(new Map());

  /**
   * Whether a chapter's scene can ever be asked for: a staff exercise needs its
   * phrases, so one with none to download never loads, and its video is not
   * held for it.
   */
  const sceneCanBeAskedFor = React.useCallback(
    (chapter: number) => {
      const [type, data] = chapterData?.[chapter]?.data ?? [];
      if (type !== ChapterType.Exercise) return false;
      const [name] = data.scene;
      const needsPhrases =
        name === ExerciseScene.StaffExercise || name === ExerciseScene.NoTimeExercise;
      return (
        !needsPhrases ||
        tutorialData.objective_data.some((objective) => objective.chapter === chapter)
      );
    },
    [chapterData, tutorialData.objective_data],
  );

  /** Chapters whose scene has been asked for at least once. */
  const scenesAskedForRef = React.useRef<ReadonlySet<number>>(new Set());
  /** The opened chapter's video may go on once its scene has been asked for. */
  const sceneAskedForChapter = React.useCallback((chapter: number) => {
    scenesAskedForRef.current = new Set([...scenesAskedForRef.current, chapter]);
    setChapterOpenedAwaitingScene((awaiting) => (awaiting === chapter ? undefined : awaiting));
  }, []);

  const CheckChapter = React.useCallback(
    (chapter: number) => {
      // NOTE: Do NOT call phaserRef.current?.scene?.scene.stop() here!
      // ScenePlugin.stop() QUEUES the stop (via queueOp), but
      // game.scene.start() in loadScene runs IMMEDIATELY. This creates a
      // race: the scene starts, then the queued stop fires on the next
      // game step and shuts it down. SceneManager.start() already handles
      // stopping a running scene inline, so the explicit stop is unnecessary.
      if (!chapterData || !tutorialData) {
        return;
      }
      const _chapter = chapterData[chapter || 0];
      if (!_chapter) {
        return;
      }
      const [type, data] = _chapter.data;
      switch (type) {
        case ChapterType.Lecture: {
          activeSceneChapterRef.current = undefined;
          // MUS-651: an exercise asked for while the game was booting is no
          // longer wanted, or it would start on a lecture once the game is ready.
          sceneLoadWaitingForGameRef.current = null;
          if (phaserLoadTimer.current) clearTimeout(phaserLoadTimer.current);
          setIsExercise(false);
          retryCountRef.current.delete(chapter);
          break;
        }
        case ChapterType.Exercise: {
          // Guard against restarting the same exercise scene that's already running.
          // This prevents scene restart loops from rapid CheckChapter calls.
          if (activeSceneChapterRef.current === chapter) {
            return;
          }
          setIsExercise(true);
          const [name, config] = data.scene;
          const updatedConfig = {
            ...config,
            cbType,
            chapterIdx: chapter,
            levelNumber: tutorialData.level_number,
            playbackSpeed,
            graded: gradedRef.current,
            objectives: config.objectives.map((obj, idx) => ({
              ...obj,
              // Where to seek the video after this objective completes
              videoSkipDestination:
                obj.videoSkipDestination ??
                timestamps.at(chapter)?.at(idx + 1)?.load ??
                chapterData.at(chapter + 1)?.start ??
                tutorialData.tutorial_config.end,
              // Narration segment: from this objective's load to its start
              narrationStartTimestamp: timestamps.at(chapter)?.at(idx)?.load,
              narrationEndTimestamp: timestamps.at(chapter)?.at(idx)?.start,
            })),
          };
          if (name === ExerciseScene.StaffExercise || name === ExerciseScene.NoTimeExercise) {
            // MUS-651: once every phrase it needs is in. They arrive one by
            // one, and one that did not download keeps only its own chapter waiting.
            if (chapterPhrasesIn(phrases, tutorialData.objective_data, chapter)) {
              retryCountRef.current.delete(chapter); // Clear retry count for this chapter
              updatedConfig.objectives = updatedConfig.objectives.map((o, idx) => ({
                ...o,
                phrases: [phrases[chapter][idx]],
              }));
              // Set guard only after phrases are confirmed loaded
              activeSceneChapterRef.current = chapter;
              console.log(
                `[EXERCISE-DBG] CheckChapter: loading scene ${name} for chapter ${chapter}`,
              );
              loadedSceneGrading(updatedConfig.graded, name);
              loadScene(name, updatedConfig);
              sceneAskedForChapter(chapter);
            } else {
              // Check retry count to avoid infinite retries
              const retryCount = retryCountRef.current.get(chapter) || 0;
              if (retryCount < 10) {
                retryCountRef.current.set(chapter, retryCount + 1);
                // Retry after a short delay if phrases aren't loaded yet
                if (checkChapterTimer.current) {
                  clearTimeout(checkChapterTimer.current);
                }
                checkChapterTimer.current = setTimeout(() => {
                  // Need to get the latest CheckChapter from the ref
                  const latestCheckChapter = checkChapterRef.current;
                  if (latestCheckChapter) {
                    latestCheckChapter(chapter);
                  }
                }, 300);
              } else {
                retryCountRef.current.delete(chapter);
              }
            }
          } else {
            retryCountRef.current.delete(chapter);
            activeSceneChapterRef.current = chapter;
            console.log(
              `[EXERCISE-DBG] CheckChapter: loading scene ${name} for chapter ${chapter} (no phrases)`,
            );
            loadedSceneGrading(updatedConfig.graded, name);
            loadScene(name, updatedConfig);
            sceneAskedForChapter(chapter);
          }
          break;
        }
      }
    },
    [
      tutorialData,
      chapterData,
      dispatch,
      loadScene,
      loadedSceneGrading,
      sceneAskedForChapter,
      phrases,
      timestamps,
      cbType,
    ],
  );

  // Store CheckChapter in a ref so it can be called from setTimeout
  const checkChapterRef = React.useRef(CheckChapter);
  React.useEffect(() => {
    checkChapterRef.current = CheckChapter;
  }, [CheckChapter]);

  const [isLoaded, setIsLoaded] = React.useState(false);
  const [initialSeekComplete, setInitialSeekComplete] = React.useState(false);
  /** Mirror for the cue callback, which fires outside the render cycle. */
  const initialSeekCompleteRef = React.useRef(false);
  React.useEffect(() => {
    initialSeekCompleteRef.current = initialSeekComplete;
  }, [initialSeekComplete]);

  // Side effect triggered on focused chapter change.
  // Uses checkChapterRef (not CheckChapter directly) so that changes to the
  // CheckChapter callback identity don't re-trigger this effect and cause
  // the scene to restart in a loop.
  React.useEffect(() => {
    // Clear any pending CheckChapter calls
    if (checkChapterTimer.current) {
      clearTimeout(checkChapterTimer.current);
    }

    // Don't call CheckChapter if an exercise is currently showing
    // This prevents interrupting the exercise when video progress updates the TOC highlight
    if (showExercise) {
      return;
    }

    checkChapterTimer.current = setTimeout(() => {
      // MUS-651: not under the end screen. Passing the last exercise takes its
      // scene down and brings Proceed up; asked for again here, the passed
      // exercise started over, out of sight, under Proceed.
      if (proceedButtonVisibilityRef.current !== 'visible') {
        checkChapterRef.current(currentChapter || 0);
      }
      if (currentChapter) {
        dispatch(
          setLastChapter({
            lesson: tutorialData.level_number,
            chapter: currentChapter,
          }),
        );
      }
      checkChapterTimer.current = undefined;
    }, 500);

    return () => {
      if (checkChapterTimer.current) {
        clearTimeout(checkChapterTimer.current);
        checkChapterTimer.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChapter, dispatch, tutorialData.level_number, showExercise]);

  const lastStartedChapterRef = React.useRef<number>(-1);

  React.useEffect(() => {
    console.log('[MUS-232] chapter-started effect', {
      currentChapter,
      last: lastStartedChapterRef.current,
    });
    if (currentChapter !== undefined && currentChapter !== lastStartedChapterRef.current) {
      lastStartedChapterRef.current = currentChapter;
      console.log('[MUS-232] dispatching tutorialChapterStarted', {
        level: tutorialData.level_number,
        chapter: currentChapter,
      });
      dispatch(tutorialChapterStartedEventAction(tutorialData.level_number, currentChapter));

      // Initiating the first tutorial chapter "starts" the level: mark a
      // never-played level as active so its roadmap node flips from grey to a
      // green ring. Guarded by currentLevelULP so we never downgrade a level
      // that's already in progress or complete.
      if (currentChapter === 0 && !levelStartedFiredRef.current && !currentLevelULP) {
        levelStartedFiredRef.current = true;
        dispatch(
          lessonActions.updateCurrentULP({
            currentUserLevelProgress: { status: LevelStatus.active },
          }),
        );
      }
    }
  }, [currentChapter, dispatch, tutorialData.level_number, currentLevelULP]);

  React.useEffect(() => {
    if (currentChapter !== undefined && currentChapter === 1) {
      // show midi blocker after first chapter so new users get a chance to
      // experience a bit of the tutorial. Give enough time for chapter to finish.
      // MUS-651: the check is armed again whenever the keyboard comes or goes,
      // so the one armed before is cancelled: it would read the keyboard state
      // from when it was armed and show the prompt with the keyboard back.
      const keyboardCheckTimer = setTimeout(() => {
        checkMidiAndShowModal();
      }, 4500);
      return () => clearTimeout(keyboardCheckTimer);
    } else if (currentChapter !== undefined && currentChapter > 1) {
      checkMidiAndShowModal();
    }
  }, [currentChapter, checkMidiAndShowModal]);

  const completedChaptersRef = React.useRef<Set<number>>(new Set());
  const previousMaxChapterRef = React.useRef<number>(-1);
  const tutorialCompletedFiredRef = React.useRef(false);
  // Chapters saved as completed (earlier sittings included), read at completion
  // time without re-creating markChapterCompleted on every save.
  const savedCompletedChaptersRef = React.useRef(completedChapters);
  savedCompletedChaptersRef.current = completedChapters;
  // Exercise objectives skipped this sitting, per chapter. A chapter still clears
  // when some are skipped; the count goes on its chapter-completed event.
  const skippedObjectivesRef = React.useRef<Map<number, number>>(new Map());

  const markChapterCompleted = React.useCallback(
    (chapterIdx: number, exercisePass?: { inputUsed: TutorialInputUsed; graded: boolean }) => {
      console.log('[MUS-232] markChapterCompleted called', {
        chapterIdx,
        alreadyCompleted: completedChaptersRef.current.has(chapterIdx),
      });
      if (completedChaptersRef.current.has(chapterIdx)) return;
      const total = chapterData?.length ?? 0;
      if (chapterIdx < 0 || chapterIdx >= total) return;
      // Complete = every chapter, whenever it was done: the tutorial completes on
      // the chapter that finishes the set, counting earlier sittings.
      const completesLevel = completesTutorial(
        [...completedChaptersRef.current, ...(savedCompletedChaptersRef.current ?? [])],
        chapterIdx,
        total,
      );
      completedChaptersRef.current.add(chapterIdx);
      console.log('[MUS-232] chapter completed', {
        level: tutorialData.level_number,
        chapter: chapterIdx,
        completedSoFar: completedChaptersRef.current.size,
        total,
      });
      dispatch(
        markChapterAsCompleted({
          lesson: tutorialData.level_number,
          chapter: chapterIdx,
        }),
      );
      dispatch(
        syncChapterComplete({
          lesson: tutorialData.level_number,
          chapter: chapterIdx,
        }),
      );
      dispatch(
        tutorialChapterCompletedEventAction(
          tutorialData.level_number,
          chapterIdx,
          skippedObjectivesRef.current.get(chapterIdx) ?? 0,
          exercisePass,
        ),
      );
      if (completesLevel && !tutorialCompletedFiredRef.current) {
        tutorialCompletedFiredRef.current = true;
        console.log('[MUS-232] all chapters completed — firing tutorialCompleted', {
          level: tutorialData.level_number,
        });
        dispatch(tutorialCompletedEventAction(tutorialData.level_number, total));
      }
    },
    [chapterData, dispatch, tutorialData.level_number],
  );

  /**
   * MUS-184: pause on an event's own timestamp rather than wherever the
   * playhead happened to be when we found out about it.
   *
   * Both notification paths are delivered as main-thread tasks, so under
   * contention they arrive late and the video has already run past the
   * pause point. Pausing the element directly (instead of waiting for the
   * `playing` prop to round-trip through a render) removes one source of
   * that lag, and rewinding removes the rest.
   */
  const pauseAtExactly = React.useCallback((targetSeconds: number) => {
    setIsVideoPlaying(false);
    // MUS-651: a stop reached under the keyboard prompt holds after it closes.
    if (videoPlayingBeforeMidiPromptRef.current) videoPlayingBeforeMidiPromptRef.current = false;
    const video = videoElementRef.current;
    if (!video) return;
    video.pause();
    const overshoot = video.currentTime - targetSeconds;
    if (overshoot > MAX_PAUSE_OVERSHOOT_SECONDS) {
      console.log(
        `[MUS-184] pause overshot by ${overshoot.toFixed(3)}s — rewinding to ${targetSeconds}`,
      );
      video.currentTime = targetSeconds;
    }
  }, []);

  /**
   * Treat everything up to `seconds` as already handled. Called whenever a
   * seek lands so that jumping forward doesn't replay the events skipped
   * over, and jumping backward re-arms the events about to be replayed.
   */
  const armEventsAt = React.useCallback(
    (seconds: number) => {
      firedEventsRef.current = keysHandledBefore(eventSchedule, seconds);
    },
    [eventSchedule],
  );

  /** Dispatch a scheduled event exactly once, whichever path noticed it first. */
  const dispatchScheduledEvent = React.useCallback(
    (item: ScheduledEvent, source: 'cue' | 'progress') => {
      if (firedEventsRef.current.has(item.key)) return;
      firedEventsRef.current.add(item.key);

      const { pause, phaserEvent } = item.event;
      console.log(
        `[MUS-184] event at=${item.event.at} type=${phaserEvent?.[0]} pause=${pause} source=${source} chapter=${item.chapterIdx}`,
      );

      if (pause) pauseAtExactly(item.at);
      if (!phaserEvent) return;

      const [rawEventType, payload] = phaserEvent;
      if (rawEventType === 'UpdateObjective') {
        // Attribute the objective to the chapter that declares the event.
        // This previously used the lookahead chapter index, so an event
        // within a second of the next chapter's start was reported against
        // the wrong chapter.
        NotifyPhaserEvents.UpdateObjective(
          typeof payload === 'string'
            ? { prompt: payload, chapter: item.chapterIdx }
            : {
                ...(typeof payload === 'object' && payload !== null ? payload : {}),
                chapter: item.chapterIdx,
              },
        );
      } else if (rawEventType === 'StartExercise') {
        NotifyPhaserEvents.StartExercise(
          typeof payload === 'object' && payload !== null ? payload : {},
        );
      } else {
        NotifyPhaser(phaserEvent);
      }
    },
    [pauseAtExactly],
  );

  /** Primary path: the media engine reports the playhead reaching an event. */
  const onCueEnter = React.useCallback(
    (key: string) => {
      const item = eventSchedule.find((candidate) => candidate.key === key);
      if (!item || !initialSeekCompleteRef.current) return;
      dispatchScheduledEvent(item, 'cue');
    },
    [eventSchedule, dispatchScheduledEvent],
  );

  useMediaEventCues({
    video: videoElement,
    schedule: eventSchedule,
    onCueEnter,
    onSeeked: armEventsAt,
  });

  const onProgress = React.useCallback(
    (progress: OnProgressProps) => {
      // An empty array is as unusable as undefined here, and previously fell
      // through to logic that treats "no chapters" as "all chapters complete".
      if (!chapterData || chapterData.length === 0) {
        console.warn('no chapter data');
        return;
      }
      const current = progress.playedSeconds;
      // MUS-651: the finished video played again from the start (the overlay
      // or Space restarts an ended video): the end screen goes, so the chapter
      // checks it holds back run again and the replay loads its exercises.
      const lastChapterStart = chapterData[chapterData.length - 1].start;
      if (proceedButtonVisibilityRef.current === 'visible' && current < getTime(lastChapterStart)) {
        setProceedButtonVisibility('hidden');
      }
      if (current >= getTime(tutorialData.tutorial_config.end)) {
        setProceedButtonVisibility('visible');

        // Mark level 0 as complete when tutorial ends AND all chapters are completed
        const allChaptersComplete = completedChapters?.size === chapterData.length;
        if (
          tutorialData.level_number === 0 &&
          allChaptersComplete &&
          !level0CompletionFiredRef.current
        ) {
          level0CompletionFiredRef.current = true;
          const now = new Date();
          dispatch(
            lessonActions.updateCurrentULP({
              currentUserLevelProgress: {
                status: LevelStatus.complete,
                level_completed_at: now.toISOString(),
                highest_tier_complete: 1,
                highest_tier_completed_at: now.toISOString(),
              },
            }),
          );
        }

        // Strict analytics signal for the final Lecture chapter: the video
        // playhead has reached the tutorial's overall end timestamp.
        if (initialSeekComplete && !isManualSeekingRef.current) {
          const lastIdx = chapterData.length - 1;
          const [lastType] = chapterData[lastIdx]?.data ?? [];
          if (lastType === ChapterType.Lecture) {
            markChapterCompleted(lastIdx);
          }
        }
      }
      const max = chapterData
        .filter((x) => getTime(x.start) <= current + 1)
        .reduce((max, chapter, idx, arr) => (chapter.start > arr[max].start ? idx : max), 0);

      // Strict analytics signal for Lecture chapters: the playhead has
      // crossed the start of a later chapter, meaning every Lecture
      // chapter from the previous max up to (but not including) this one
      // had its end-timestamp reached during natural playback. Skip during
      // initial seek and manual scrub so jumps don't credit unwatched
      // chapters as completed.
      if (initialSeekComplete && !isManualSeekingRef.current) {
        for (let i = previousMaxChapterRef.current; i < max; i++) {
          if (i < 0) continue;
          const [chapterType] = chapterData[i]?.data ?? [];
          console.log('[MUS-232] onProgress boundary crossed', {
            i,
            max,
            chapterType: chapterType === ChapterType.Lecture ? 'Lecture' : 'Exercise',
            initialSeekComplete,
            isManualSeeking: isManualSeekingRef.current,
          });
          if (chapterType === ChapterType.Lecture) {
            markChapterCompleted(i);
          }
        }
      }
      // Always advance the high-water mark, even when gated. Otherwise a
      // manual seek that jumps ahead would re-fire completions for every
      // skipped chapter the moment the gate reopens.
      previousMaxChapterRef.current = Math.max(previousMaxChapterRef.current, max);

      // Don't update currentChapter from onProgress until initial seek is complete
      // (prevents overriding the saved lastChapter when video first loads), and don't
      // update during a manual seek (prevents overriding the user's tapped chapter
      // until the video actually starts playing at the new position).
      if (initialSeekComplete && !isManualSeekingRef.current) {
        setCurrentChapter(max);
      }
      //console.log("onProgress: setting chapter to ", max);
      // MUS-184 backstop. Media cues are the primary trigger for exercise
      // events, but this covers any engine that doesn't deliver them.
      // Edge-triggered on purpose: fire everything the playhead has passed
      // and that hasn't fired yet, so an observation arriving late still
      // recovers the event instead of dropping it the way the old 200ms
      // match window did. dispatchScheduledEvent de-duplicates against the
      // cue path, so at most one of the two actually dispatches.
      if (initialSeekComplete) {
        takeThroughFirstPause(
          selectDueEvents(eventSchedule, current, firedEventsRef.current),
        ).forEach((item) => dispatchScheduledEvent(item, 'progress'));
      }
    },
    [
      eventSchedule,
      dispatchScheduledEvent,
      chapterData,
      completedChapters,
      tutorialData.tutorial_config.end,
      tutorialData.level_number,
      currentChapter,
      initialSeekComplete,
      dispatch,
      markChapterCompleted,
    ],
  );

  const playVideo = React.useCallback(() => {
    setIsVideoPlaying(true);
  }, []);

  const resume = React.useCallback(() => {
    setManualPause(false);
    // MUS-651: with no exercise on screen, the tap is a request to play the
    // video. Clearing the pause alone left a video the app had stopped itself
    // (opened before any tap, a refused autoplay, a stop at an exercise that
    // never appeared) stopped, with no overlay and nothing left to tap. During
    // an exercise the video stays paused.
    if (!showExerciseRef.current) setIsVideoPlaying(true);
    NotifyPhaserEvents.ResumeExercise();
  }, []);

  const _lastPlayedNoteTimer = React.useRef<NodeJS.Timeout | undefined>(undefined);

  const noteOn = React.useCallback((ev: { note: number; velocity: number; tap?: boolean }) => {
    console.log(
      '[MUS-208] InteractiveTemplate1 noteOn called, note:',
      ev.note,
      'velocity:',
      ev.velocity,
    );
    // A tap says so: only the exercises that take taps hear it.
    NotifyPhaserEvents.MidiOn({ note: ev.note, velocity: ev.velocity, ...tapMark(ev) });
  }, []);

  const noteOff = React.useCallback(
    (ev: { note: number; tap?: boolean }) =>
      NotifyPhaserEvents.MidiOff({ note: ev.note, ...tapMark(ev) }),
    [],
  );

  const listener = React.useRef({ name: 'eventListener', noteOn, noteOff });
  const { addListener, removeListener, clearListeners } = useMidiContext();

  React.useEffect(() => {
    // NOTE: This is entirely dependant on the video ALWAYS resuming whenever an exercise gets unloaded
    // due to whatever reason may be (the exercise being completed succesfully / skipped or jumping
    // to a completely different chapter). Point is: if the video playback state doesn't change, this
    // breaks.
    if (isVideoPlaying) {
      setSkipButtonVisibility((prev) => {
        if (prev !== undefined) return 'hidden';
        else return undefined;
      });
      setNextRing(null);
    }
  }, [isVideoPlaying]);

  const onVideoClick = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    Tone.start();
    if ((e.target as HTMLElement).tagName === 'VIDEO') {
      setManualPause(true);
      NotifyPhaserEvents.PauseExercise();
    }
  }, []);

  const jumpTo = React.useCallback(
    (seconds: number, chapter?: number) => {
      // Cancel any pending CheckChapter from the currentChapter effect to
      // prevent it from racing with the CheckChapter call below.
      if (checkChapterTimer.current) {
        clearTimeout(checkChapterTimer.current);
        checkChapterTimer.current = undefined;
      }
      // Block onProgress from overriding the tapped chapter until the seek
      // actually completes. Cleared by onSeek when ReactPlayer fires seeked.
      isManualSeekingRef.current = true;

      console.log('[EXERCISE-DBG] setIsVideoPlaying(false) from jumpTo');
      setIsVideoPlaying(false);
      setNextRing(null);
      // The jump holds the video itself until its scene is asked for.
      setChapterOpenedAwaitingScene(undefined);
      setManualPause(false);
      setProceedButtonVisibility('hidden');
      if (videoRef && videoRef.current) videoRef.current.seekTo(seconds);
      // Re-arm immediately rather than waiting for the element's `seeked`
      // event, so a progress tick landing in between can't replay every
      // event before the jump target.
      armEventsAt(seconds);

      if (chapter !== undefined) {
        // If the scene for this chapter is already loaded, just seek the
        // video — don't restart the Phaser scene. The video events
        // (UpdateObjective, StartExercise) will fire once playback resumes
        // and the already-running scene will handle them. Restarting the
        // scene here races with the currentChapter effect's CheckChapter
        // call and can leave the exercise in a half-initialized state.
        // Unload the current exercise (stops metronome, clears timers).
        // Send UnloadExercise whenever a Phaser scene is loaded — not
        // just when showExercise is true. The scene can be loaded by the
        // currentChapter effect before any video events fire, leaving it
        // running even though showExercise is still false.
        if (showExercise || activeSceneChapterRef.current !== undefined) {
          NotifyPhaserEvents.UnloadExercise();
        }
        setShowExercise(false);
        // Clear the active scene guard so CheckChapter doesn't skip
        // the new chapter thinking it's already loaded.
        activeSceneChapterRef.current = undefined;
        setCurrentChapter(chapter);
        // Call CheckChapter after a frame so Phaser can process the
        // scene stop from UnloadExercise before starting the new scene.
        // Without this delay, the old scene's shutdown can interfere
        // with the new scene's initialization.
        // MUS-576: the video must not resume before that call has been made,
        // or it can reach the chapter's first UpdateObjective while no scene
        // exists to receive it. The gate holds a seek that completes first.
        sceneLoadGateRef.current.loadRequested();
        requestAnimationFrame(() => {
          checkChapterRef.current(chapter);
          // MUS-576: save the resume chapter where the chapter is loaded. The
          // currentChapter effect saves it 500 ms later and only while no
          // exercise is showing, which a jump onto an exercise does not wait
          // for.
          if (chapter) {
            dispatch(setLastChapter({ lesson: tutorialData.level_number, chapter }));
          }
          if (sceneLoadGateRef.current.loadIssued()) setIsVideoPlaying(true);
        });
      } else {
        setShowExercise(false);
      }
    },
    [CheckChapter, showExercise, armEventsAt, dispatch, tutorialData.level_number],
  );

  const jumpToChapter = React.useCallback(
    (seconds: number, chapter?: number) => {
      jumpTo(seconds, chapter);
    },
    [jumpTo],
  );

  // Tap input: an exercise played with any key, the app unable to hear the
  // player. While it runs, a tap on the exercise area or a computer key plays.
  const gameLayerRef = React.useRef<HTMLDivElement | null>(null);
  const tapInputListening =
    !sceneGraded &&
    sceneTakesTaps(loadedSceneName) &&
    showExercise &&
    !manualPause &&
    !exercisePaused;
  const tapInputListeningRef = React.useRef(tapInputListening);
  tapInputListeningRef.current = tapInputListening;
  useTapInput({
    listening: tapInputListening,
    area: gameLayerRef,
    noteOn: tapNoteOn,
    noteOff: tapNoteOff,
  });
  // A tap exercise is on screen, playing or paused: a control that has been
  // used hands focus back to the page, so Space then plays (or, paused,
  // resumes as the overlay says) rather than pressing the control again.
  const tapExerciseOnScreen = !sceneGraded && sceneTakesTaps(loadedSceneName) && showExercise;
  useReturnFocusAfterControlUse(tapExerciseOnScreen, gameLayerRef);

  // Spacebar event listener
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // While taps play, Space and Enter are taps; Escape pauses instead.
      if (tapInputListeningRef.current) {
        if (e.key !== 'Escape') return;
        setManualPause(true);
        NotifyPhaserEvents.ToggleExercise();
        return;
      }
      if (e.key === ' ' || e.key === 'Enter') {
        setManualPause((pause) => !pause);
        NotifyPhaserEvents.ToggleExercise();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Refs so the ExercisePassed listener always reads current values without
  // forcing the effect to re-register (which risks dropping the event).
  const currentChapterRef = React.useRef(currentChapter);
  currentChapterRef.current = currentChapter;
  const chapterDataRef = React.useRef(chapterData);
  chapterDataRef.current = chapterData;
  const jumpToChapterRef = React.useRef(jumpToChapter);
  jumpToChapterRef.current = jumpToChapter;
  const armEventsAtRef = React.useRef(armEventsAt);
  armEventsAtRef.current = armEventsAt;

  // MUS-651: a tutorial step never sits blank. The video not ready, the game
  // not ready, or the video stopped with no exercise, no pause overlay, no
  // Proceed and no MIDI prompt on screen (an exercise that never loaded): held
  // for TUTORIAL_STEP_TIMEOUT_MS of visible time, the step shows a message with
  // Retry and the way back.
  const [videoPlayerKey, setVideoPlayerKey] = React.useState(0);
  const [stepRetryCount, setStepRetryCount] = React.useState(0);
  const videoStoppedWithNothingOnScreen =
    !isVideoPlaying &&
    !showExercise &&
    !manualPause &&
    proceedButtonVisibility === 'hidden' &&
    !showMidiModal;
  const tutorialStepStuck =
    !isLoaded || !phaserReady || videoHeld || videoStoppedWithNothingOnScreen;
  // Not over the offline screen or the paywall, which each give the way on.
  const { allowed: didNotLoadMessageAllowed } = useDidNotLoadMessageAllowed(userSubscriptionStatus);
  // MUS-651: a chapter whose phrase did not download cannot show its exercise:
  // once its video stops where the exercise would start, the step says so at once.
  const stoppedForPhraseThatFailed =
    isLoaded &&
    phaserReady &&
    videoStoppedWithNothingOnScreen &&
    chapterNeedsAny(phrasesFailed, currentChapter);
  const tutorialStepStalled =
    (useStalledFor(
      tutorialStepStuck,
      TUTORIAL_STEP_TIMEOUT_MS,
      `${currentChapter ?? 0}:${stepRetryCount}`,
    ) ||
      stoppedForPhraseThatFailed) &&
    didNotLoadMessageAllowed;
  /** A chapter that starts over once its phrases are back (see retryTutorialStep). */
  const [chapterWaitingForPhrases, setChapterWaitingForPhrases] = React.useState<number>();
  const retryTutorialStep = React.useCallback(() => {
    setStepRetryCount((count) => count + 1);
    // The game never reported ready: starting the chapter over would only wait
    // for it again. Ask it to report again; the chapter's scene starts then.
    if (!phaserReady) restartGameBoot(phaserRef.current?.game);
    if (!isLoaded) {
      // Load the video again from scratch.
      setVideoPlayerKey((key) => key + 1);
      return;
    }
    const chapter = currentChapterRef.current ?? 0;
    // MUS-651: the phrases that did not download are asked for once more (no
    // further tries: the player is waiting). A chapter that needs one starts
    // over once they are in; if one fails again, the message is back.
    if (phrasesFailed.length > 0) {
      const retrying = phrasesFailed;
      setPhrasesRetrying(retrying);
      setPhrasesFailed([]);
      void downloadPhrases(retrying, []).finally(() => {
        if (!isUnmountedRef.current) setPhrasesRetrying([]);
      });
      if (chapterNeedsAny(retrying, chapter)) {
        setChapterWaitingForPhrases(chapter);
        return;
      }
    }
    // Start the chapter over: seek to its start and load its exercise again.
    const chapterStart = chapterDataRef.current?.[chapter]?.start;
    jumpToChapterRef.current(chapterStart ? getTime(chapterStart) : 0, chapter);
  }, [isLoaded, phaserReady, phrasesFailed, downloadPhrases]);
  React.useEffect(() => {
    if (chapterWaitingForPhrases === undefined || phrasesRetrying.length > 0) return;
    setChapterWaitingForPhrases(undefined);
    if (chapterNeedsAny(phrasesFailed, chapterWaitingForPhrases)) return;
    const chapterStart = chapterDataRef.current?.[chapterWaitingForPhrases]?.start;
    jumpToChapterRef.current(chapterStart ? getTime(chapterStart) : 0, chapterWaitingForPhrases);
  }, [chapterWaitingForPhrases, phrasesRetrying, phrasesFailed]);
  // MUS-651: the last phrase an exercise chapter on screen was waiting for has
  // arrived: ask for its scene now (the video is waiting for it), not only on
  // the next of CheckChapter's retries, which stop after a few seconds. Not
  // during a chapter jump, which asks for the scene itself a frame later, nor
  // for a chapter that starts over once its phrases are back (Retry). Only for
  // a chapter whose scene was never asked for: one already asked for had its
  // phrases (they are never taken back), and a passed exercise must not start
  // again under the end screen because some other phrase landed late.
  React.useEffect(() => {
    const chapter = currentChapterRef.current ?? 0;
    if (chapterWaitingForPhrases !== undefined) return;
    if (scenesAskedForRef.current.has(chapter)) return;
    if (proceedButtonVisibilityRef.current === 'visible') return;
    if (showExerciseRef.current || isManualSeekingRef.current) return;
    if (activeSceneChapterRef.current === chapter) return;
    if (!chapterPhrasesIn(phrases, tutorialData.objective_data, chapter)) return;
    checkChapterRef.current(chapter);
  }, [phrases, tutorialData.objective_data, chapterWaitingForPhrases]);
  const leaveTutorialStepForRoadmap = React.useCallback(() => {
    NotifyPhaserEvents.UnloadExercise();
    navigate('/roadmap');
  }, [navigate]);
  const markChapterCompletedRef = React.useRef(markChapterCompleted);
  markChapterCompletedRef.current = markChapterCompleted;

  // Mark an exercise as complete when all of its objectives have been completed
  React.useEffect(() => {
    PhaserToReactListeners.ExercisePassed((passed) => {
      const chapter = chapterOfActiveExercise(
        activeSceneChapterRef.current,
        currentChapterRef.current,
      );
      const chapters = chapterDataRef.current;
      console.log(`[EXERCISE-DBG] ExercisePassed fired chapter=${chapter}`);
      markChapterCompleted(chapter, {
        inputUsed: passed?.inputUsed ?? inputHeardWith(sceneGradedRef.current),
        graded: true,
      });
      setShowExercise(false);
      setExercisePaused(true);

      // For the final exercise in a level, additionally show the proceed
      // button and resume playback. The normal proceed-button trigger
      // (video reaching its end timestamp) never fires because the video
      // is paused during the exercise and there is no next chapter.
      const isLastChapter = chapters && chapter === chapters.length - 1;
      if (isLastChapter) {
        activeSceneChapterRef.current = undefined;
        setManualPause(false);
        playVideo();
        setProceedButtonVisibility('visible');
      }
    });
    return () => {
      RemoveListener('ExercisePassed');
    };
  }, [dispatch, playVideo, tutorialData.level_number, markChapterCompleted]);

  const proceed = React.useCallback(() => {
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;

    console.log('[InteractiveTemplate1] proceed - cleaning up before navigation');
    NotifyPhaserEvents.UnloadExercise();

    setTimeout(() => {
      if (tutorialData.level_number === 0) {
        // Level 0 completion is already fired in onProgress when tutorial ends
        dispatch(setLevelSelect({ levelSelect: 1 }));
        navigate('/tutorial');
      } else {
        navigate('/lesson');
      }
    }, 100);
  }, [dispatch, navigate, tutorialData.level_number]);

  // Narration between two objectives of the same exercise: the scene asks for
  // the video segment that leads into the next objective.
  const getNarrationVideo = React.useCallback(() => videoElementRef.current, []);
  const seekToNarration = React.useCallback(
    (seconds: number) => {
      videoRef.current?.seekTo(seconds);
      // Re-arm now rather than on the element's `seeked` event (as jumpTo
      // does), so a progress tick landing in between cannot replay the cues
      // before the landing position.
      armEventsAt(seconds);
    },
    [armEventsAt],
  );
  useVideoNarration({ getVideo: getNarrationVideo, seekTo: seekToNarration, play: playVideo });

  // Listener for "ObjectiveComplete" events
  React.useEffect(() => {
    PhaserToReactListeners.ObjectiveComplete(({ jumpTo }: { jumpTo: Timestamp }) => {
      const seconds = getTime(jumpTo);
      // MUS-576: passing the LAST objective ends the exercise, exactly as
      // skipping it does below, and has the same race: seek to the next
      // chapter, resume, and that chapter's UpdateObjective can fire before
      // its scene is loaded (the loader waits for the playhead to change
      // chapter and then for its debounce), so the exercise never loads. Take
      // the same chapter-jump path as the skip: it unloads this scene, asks for
      // the next one on the next animation frame, and does not let the video
      // resume before that.
      const nextChapter = chapterAfterExercise(
        chapterOfActiveExercise(activeSceneChapterRef.current, currentChapterRef.current),
        chapterDataRef.current?.length ?? 0,
      );
      if (nextChapter !== undefined) {
        console.log(`[EXERCISE-DBG] ObjectiveComplete: jumping to chapter ${nextChapter}`);
        jumpToChapterRef.current(seconds, nextChapter);
        return;
      }
      // Last chapter of the level: nothing to load, just move the video on.
      console.log(`[EXERCISE-DBG] ObjectiveComplete: seeking to ${jumpTo}, will play in 500ms`);
      videoRef.current?.seekTo(seconds);
      // MUS-626: re-arm now, as jumpTo does. In the apps the later phrases are
      // narrated from the audio file, so their events never fired; a progress
      // tick before `seeked` would replay them and the next phrase's
      // StartExercise would pause the video short of its end card.
      armEventsAtRef.current(seconds);
      setTimeout(() => {
        console.log('[EXERCISE-DBG] ObjectiveComplete: playing video now');
        playVideo();
      }, 500);
    });
    PhaserToReactListeners.ObjectiveSkipped(
      ({
        jumpTo,
        isLastObjective,
        inputUsed,
      }: {
        jumpTo: Timestamp;
        isLastObjective: boolean;
        inputUsed?: TutorialInputUsed;
      }) => {
        const seconds = getTime(jumpTo);
        const skippedChapter = chapterOfActiveExercise(
          activeSceneChapterRef.current,
          currentChapterRef.current,
        );
        skippedObjectivesRef.current.set(
          skippedChapter,
          (skippedObjectivesRef.current.get(skippedChapter) ?? 0) + 1,
        );
        // Skipping the last objective ends the exercise without a pass, so the
        // chapter used to stay uncleared. Clearing stays lenient: it clears, and
        // its event records how many objectives were skipped.
        if (isLastObjective) {
          markChapterCompletedRef.current(skippedChapter, {
            inputUsed: inputUsed ?? inputHeardWith(sceneGradedRef.current),
            graded: false,
          });
        }
        // Skipping the LAST objective ends the exercise and jumps to the next
        // chapter. If that chapter is another exercise immediately gated by
        // this one, the natural seek+play path races the next chapter's
        // UpdateObjective event (it fires before the scene is loaded, so the
        // exercise never loads). Route through jumpToChapter — the same path
        // used by manual TOC navigation — which unloads the current scene,
        // clears the scene guard, sets the chapter, and loads the next scene
        // before playback resumes.
        if (isLastObjective) {
          const nextChapter = chapterAfterExercise(
            chapterOfActiveExercise(activeSceneChapterRef.current, currentChapterRef.current),
            chapterDataRef.current?.length ?? 0,
          );
          if (nextChapter !== undefined) {
            jumpToChapterRef.current(seconds, nextChapter);
            return;
          }
        }
        // Within-exercise objective skip (the running scene's machine loads
        // the next objective) or no next chapter: just advance the video.
        videoRef.current?.seekTo(seconds);
        // MUS-626: skipping the last phrase of the last chapter has the same
        // stale events as passing it. Not for a within-exercise skip: its
        // destination is the next objective's UpdateObjective, which must fire.
        if (isLastObjective) armEventsAtRef.current(seconds);
        playVideo();
      },
    );
    return () => {
      RemoveListener('ObjectiveComplete');
      RemoveListener('ObjectiveSkipped');
    };
  }, [playVideo]);

  // Other phaser event listeners
  React.useEffect(() => {
    // When the singleton game is reused, PhaserReady won't fire again.
    // Set phaserReady immediately so video playback isn't blocked.
    if (phaserRef.current?.wasReused) {
      gameReadyRef.current = true;
      setPhaserReady(true);
    }
    // MUS-651: the game was rebuilt (its WebGL context was lost while the page
    // was hidden), and the chapter's scene was left orphaned in the old game,
    // off screen (that game is never torn down: its loop is asleep, so its
    // pending destroy never runs). Nothing loaded the scene into the new game:
    // the pause overlay came back, and a tap on it left an empty exercise box
    // over a video stopped at the exercise. Load the scene into the new game
    // and rewind the video to the chapter's start, so the scene hears the
    // chapter's objectives from the first one (it counts them from there). The
    // video waits under the pause overlay; a tap, Space or Enter plays it on to
    // the exercise. The video was stopped at the exercise, so it is marked as
    // playing again here: Space and Enter only take the overlay down.
    const reloadChapterOnRebuiltGame = () => {
      const chapter = activeSceneChapterRef.current;
      if (chapter === undefined) return;
      console.log(`[EXERCISE-DBG] game rebuilt: reloading chapter ${chapter}`);
      activeSceneChapterRef.current = undefined;
      setShowExercise(false);
      setExercisePaused(true);
      setManualPause(true);
      setIsVideoPlaying(true);
      const chapterStart = chapterDataRef.current?.[chapter]?.start;
      const seconds = chapterStart ? getTime(chapterStart) : 0;
      videoRef.current?.seekTo(seconds);
      armEventsAtRef.current(seconds);
      checkChapterRef.current(chapter);
      // Held paused, as the overlay says, so Space / Enter (which flips the
      // scene's pause) resumes it rather than pausing it and putting the
      // overlay straight back.
      NotifyPhaserEvents.PauseExercise();
    };
    const handlePhaserReady = () => {
      // Only a rebuilt game reports ready a second time.
      if (gameReadyRef.current) {
        queueMicrotask(reloadChapterOnRebuiltGame);
        return;
      }
      gameReadyRef.current = true;
      // MUS-651: a chapter opened while the game was booting had its scene
      // left to the 500 ms retry, while the video (held only for phaserReady)
      // resumed now. A chapter whose first UpdateObjective came sooner lost
      // it, and the video then stopped at StartExercise with no exercise on
      // screen. Start the waiting scene first, then let the video play. In a
      // microtask, because this runs inside the boot scene's create().
      const waiting = sceneLoadWaitingForGameRef.current;
      if (!waiting) {
        setPhaserReady(true);
        return;
      }
      queueMicrotask(() => {
        try {
          loadScene(waiting.name, waiting.props);
        } finally {
          setPhaserReady(true);
        }
      });
    };
    const handleShowExercise = () => {
      console.log('[EXERCISE-DBG] ShowExercise: showing overlay');
      setShowExercise(true);
      setExercisePaused(false);
    };
    const handleExercisePaused = () => {
      console.log('[EXERCISE-DBG] ExercisePaused');
      setExercisePaused(true);
      // MUS-552: a pause can start in the scene (the suspend guard), not only
      // from the controls here. Mirror it so the play/pause overlay shows and
      // the next Space / Enter resumes rather than toggling out of step.
      setManualPause(true);
    };
    const handleHideExercise = () => {
      console.log('[EXERCISE-DBG] HideExercise: hiding overlay, clearing activeSceneChapter');
      activeSceneChapterRef.current = undefined;
      setNextRing(null);
      setExercisePaused(true);
      setShowExercise(false);
    };
    const handleExerciseResumed = () => {
      setExercisePaused(false);
    };
    const handleSkipButtonRequest = () => setSkipButtonVisibility('visible');
    const handleNextRingStart = ({ durationMs }: { durationMs: number }) => {
      if (sceneGradedRef.current) return;
      nextRingCountRef.current += 1;
      setNextRing({ id: nextRingCountRef.current, durationMs });
    };

    PhaserToReactListeners.PhaserReady(handlePhaserReady);
    PhaserToReactListeners.ShowExercise(handleShowExercise);
    PhaserToReactListeners.ExercisePaused(handleExercisePaused);
    PhaserToReactListeners.HideExercise(handleHideExercise);
    PhaserToReactListeners.ExerciseResumed(handleExerciseResumed);
    PhaserToReactListeners.SkipButtonRequest(handleSkipButtonRequest);
    PhaserToReactListeners.NextRingStart(handleNextRingStart);

    return () => {
      RemoveListener('PhaserReady', handlePhaserReady);
      RemoveListener('HideExercise', handleHideExercise);
      RemoveListener('ExercisePaused', handleExercisePaused);
      RemoveListener('ExerciseResumed', handleExerciseResumed);
      RemoveListener('ShowExercise', handleShowExercise);
      RemoveListener('SkipButtonRequest', handleSkipButtonRequest);
      RemoveListener('NextRingStart', handleNextRingStart);
    };
  }, [loadScene]);

  /** The background pause in use, told when the player taps the overlay away. */
  const backgroundPauseRef = React.useRef<TutorialBackgroundPause | undefined>(undefined);
  React.useEffect(() => {
    // MUS-625: the decision is taken in a pure helper so it is unit tested
    // against the real exercise scenes. See backgroundPause.spec.ts.
    const backgroundPause = makeTutorialBackgroundPause({
      isPauseOverlayShown: () => manualPauseRef.current,
      isExerciseShowing: () => showExerciseRef.current,
      showPauseOverlay: () => setManualPause(true),
      hidePauseOverlay: () => setManualPause(false),
      pauseExercise: () => NotifyPhaserEvents.PauseExercise(),
      resumeExercise: () => NotifyPhaserEvents.ResumeExercise(),
      holdsVideoOnReturn: Capacitor.getPlatform() === 'android',
      pauseVideo: () => videoElementOf(videoRef.current)?.pause(),
      isProceedShowing: () => proceedButtonVisibilityRef.current === 'visible',
    });
    backgroundPauseRef.current = backgroundPause;

    const pauseForBackground = (reason: string) => {
      console.log('[MUS-208] pauseForBackground:', reason);
      backgroundPause.onBackground();
    };

    const resumeFromBackground = (reason: string) => {
      console.log('[MUS-208] resumeFromBackground:', reason);
      backgroundPause.onForeground();
    };

    const handleVisibilityChange = () => {
      console.log('[MUS-208] visibilitychange, hidden:', document.hidden);
      if (document.hidden) {
        pauseForBackground('visibilitychange');
      } else {
        resumeFromBackground('visibilitychange');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Android: a call or another app taking the audio while the tutorial stays on screen:
    // paused as for the background, under the overlay, until the player taps it.
    const handleNativeAudioInterrupted = () => pauseForBackground('NativeAudioInterrupted');
    const pausesOnNativeInterruption = Capacitor.getPlatform() === 'android';
    if (pausesOnNativeInterruption) {
      window.addEventListener('NativeAudioInterrupted', handleNativeAudioInterrupted);
    }

    let appStateHandle: PluginListenerHandle | undefined;
    let cancelled = false;
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          pauseForBackground('appStateChange');
        } else {
          resumeFromBackground('appStateChange');
        }
      })
        .then((handle) => {
          if (cancelled) void handle.remove();
          else appStateHandle = handle;
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (pausesOnNativeInterruption) {
        window.removeEventListener('NativeAudioInterrupted', handleNativeAudioInterrupted);
      }
      appStateHandle?.remove?.();
    };
  }, []);

  // A tap on the pause overlay plays a video that was playing when the app went
  // to the background. It starts the element here, inside the tap, rather than
  // only through the player's `playing` prop: on Android the WebView stops the
  // video itself when the app takes the audio focus back on its return, and
  // the player component can still think it is playing, so a start left to the
  // next render may never be made. It is gated as the `playing` prop is: not
  // before the game is ready (the prop starts it then), and during an exercise
  // the video stays paused, as before.
  const resumeFromPauseOverlay = React.useCallback(() => {
    if (isVideoPlaying && phaserReady && !videoHeld && !showExerciseRef.current) {
      const element = videoElementOf(videoRef.current);
      if (element?.paused) element.play().catch(() => undefined);
    }
    backgroundPauseRef.current?.onPlayerResumed();
    resume();
  }, [isVideoPlaying, phaserReady, videoHeld, resume]);

  // Pausing on tab/window hide is already handled by the visibilitychange
  // and Capacitor appStateChange listeners above.  A previous `window.blur`
  // handler existed here but it fired spuriously (DevTools focus, exercise
  // setup focus shifts) and immediately paused exercises before the user
  // could interact.  Removed as part of MUS-145.

  React.useEffect(() => {
    clearListeners();
    const listenerInstance = listener.current;
    addListener(listenerInstance);
    return () => removeListener(listenerInstance);
  }, [addListener, removeListener, clearListeners]);

  // Cleanup all timers and Phaser scene on unmount
  React.useEffect(() => {
    return () => {
      console.log('[InteractiveTemplate1] Component unmounting - cleaning up');

      // Normal mode: the game is destroyed by PhaserGame on unmount, so
      // we just need to unload the exercise and clear timers.
      NotifyPhaserEvents.UnloadExercise();

      // Clear all timers
      if (phaserLoadTimer.current) {
        clearTimeout(phaserLoadTimer.current);
        phaserLoadTimer.current = undefined;
      }
      if (checkChapterTimer.current) {
        clearTimeout(checkChapterTimer.current);
        checkChapterTimer.current = undefined;
      }

      console.log('[InteractiveTemplate1] Cleanup complete');
    };
  }, []);

  // Ensure immediate stop when global manual pause turns true
  /*React.useEffect(() => {
    if (manualPause) setIsPlaying(false);
  }, [manualPause]);*/

  const aspectRatio = 1.777778;
  // Keeps track of the <video> elements width and height which is used later for scaling the canvas
  // to fit the size of the video
  const [videoPlayerSize, setVideoPlayerSize] = React.useState({
    width: 0,
    height: 0,
  });
  const [videoLoaded, setVideoLoaded] = React.useState(false);

  const resizeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const onResize = React.useCallback(
    (ratio: number) => {
      const vid: HTMLVideoElement = document.getElementsByTagName('video')[0];
      if (!vid) return;
      // The exact size, not clientWidth / clientHeight: those are rounded to
      // whole pixels, and the pause overlay sized from them missed a sliver of
      // the video at its edges.
      setVideoPlayerSize(containedPictureSize(exactSizeOf(vid), ratio));
    },
    [setVideoPlayerSize],
  );

  // MUS-651: whether the video may start now (the `playing` prop's gates), for
  // the load callback, and an autoplay that waited for them.
  const videoMayStart = phaserReady && !manualPause && !videoHeld;
  const videoMayStartRef = React.useRef(videoMayStart);
  videoMayStartRef.current = videoMayStart;
  const [autoplayDeferred, setAutoplayDeferred] = React.useState(false);
  React.useEffect(() => {
    if (!autoplayDeferred || !isVideoPlaying || !videoMayStart) return;
    setAutoplayDeferred(false);
    const element = videoElementOf(videoRef.current);
    if (!element?.paused) return;
    element.play().catch(() => {
      console.log('[EXERCISE-DBG] setIsVideoPlaying(false) from deferred autoplay catch');
      setIsVideoPlaying(false);
    });
  }, [autoplayDeferred, isVideoPlaying, videoMayStart]);

  const onVideoLoad = React.useCallback(() => {
    if (!isLoaded) {
      setIsLoaded(true);
      const vid: HTMLVideoElement = document.getElementsByTagName('video')[0];
      // Hand the element to the cue scheduler (MUS-184).
      setVideoElement(vid ?? null);
      // MUS-651: opened at an exercise chapter whose scene has not been asked
      // for yet: the video waits for it (see videoWaitsForScene).
      const openedChapter = currentChapter ?? 0;
      const [openedChapterType] = chapterData?.[openedChapter]?.data ?? [];
      const openedAtExerciseWithoutScene =
        openedChapterType === ChapterType.Exercise &&
        activeSceneChapterRef.current !== openedChapter &&
        sceneCanBeAskedFor(openedChapter);
      if (openedAtExerciseWithoutScene) {
        setChapterOpenedAwaitingScene(openedChapter);
        // Asked for now rather than after the chapter's half-second debounce.
        // A game still booting starts it once it is ready, before the video.
        checkChapterRef.current(openedChapter);
      }
      if (autoplay) {
        // MUS-651: started here only when the player is free to play it now.
        // Started before the game was ready, it ran past the opened chapter's
        // first objective while the scene was still waiting for the game. The
        // start is otherwise left until it is free (see autoplayDeferred).
        if (videoMayStartRef.current && !openedAtExerciseWithoutScene) {
          vid.play().catch(() => {
            console.log('[EXERCISE-DBG] setIsVideoPlaying(false) from vid.play catch');
            setIsVideoPlaying(false);
          });
        } else {
          setAutoplayDeferred(true);
        }
      } else {
        console.log('[EXERCISE-DBG] setIsVideoPlaying(false) from onVideoLoad !autoplay');
        setIsVideoPlaying(false);
        setExercisePaused(true);
      }
      onResize(pictureRatioOf(vid, aspectRatio));
      setVideoLoaded(true);
      if (chapterData && currentChapter) {
        if (videoRef && videoRef.current) {
          const resumeAt = getTime((chapterData[currentChapter] || chapterData[0]).start);
          videoRef.current.seekTo(resumeAt);
          // Arm here rather than relying on the element's `seeked` event: the
          // listener for that is attached on the render after videoElement is
          // set, which can be later than this seek. Without arming, resuming
          // at a saved chapter would replay every event before it (MUS-184).
          armEventsAt(resumeAt);
          // Mark initial seek as complete after a short delay to allow seek to finish
          setTimeout(() => {
            setInitialSeekComplete(true);
          }, 100);
        }
      } else {
        // No saved chapter, allow onProgress to update immediately
        setInitialSeekComplete(true);
      }
    }
  }, [
    onResize,
    chapterData,
    isLoaded,
    currentChapter,
    autoplay,
    armEventsAt,
    aspectRatio,
    sceneCanBeAskedFor,
  ]);

  /** Refit to the video on screen, at its own aspect ratio. */
  const refitToVideo = React.useCallback(() => {
    const vid: HTMLVideoElement | undefined = document.getElementsByTagName('video')[0];
    if (vid) onResize(pictureRatioOf(vid, aspectRatio));
  }, [onResize, aspectRatio]);

  // Refit whenever the video's box changes size. A window resize event alone
  // came too early on phones, where the box eases to its new size over 0.3 s:
  // the overlay kept the old size and left a band of the video uncovered.
  const videoBoxRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const box = videoBoxRef.current;
    if (box && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(refitToVideo);
      observer.observe(box);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', refitToVideo);
    return () => {
      window.removeEventListener('resize', refitToVideo);
    };
  }, [refitToVideo]);

  // Check if an element is within the viewport without any clipping
  const isVisible = (el: Element) => {
    const rect = el.getBoundingClientRect();
    return rect.top >= 0 && rect.bottom <= window.innerHeight;
  };

  // Scroll to current chapter
  React.useEffect(() => {
    const el = document.querySelector(`#chapter-${currentChapter}`);
    if (el && !isVisible(el)) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentChapter]);

  // On mobile, the video/exercise container shrinks to make room for the
  // chapters panel when it slides in, and expands to fill the screen when
  // the panel is hidden.
  const vidContainerWidth = chaptersVisible
    ? 'min(calc(100vw * 0.67), 100vw - 20px - var(--header-height))'
    : 'calc((100vh - 20px) * 1.777778)';
  const vidContainerHeight = chaptersVisible
    ? 'min(calc(100vw * 0.67 / 1.777778), 100vh - 20px - var(--header-height))'
    : 'calc(100vh - 20px)';

  const videoContainerStylesMobile = {
    right: chaptersVisible ? '10px' : `calc(100vw / 2 - (${vidContainerWidth} / 2))`,
    bottom: '10px',
    clipPath: 'inset(0px 0px 0px 0px round 10px)',
    width: vidContainerWidth,
    height: vidContainerHeight,
  };

  // Resize the Phaser canvas (and refit the video) when the chapters panel
  // toggles on mobile, so the exercise overlay tracks the video dimensions.
  useEffect(() => {
    if (isMobile) {
      if (phaserRef.current && phaserRef.current.game) {
        phaserRef.current.game.canvas.style.width = vidContainerWidth;
        phaserRef.current.game.canvas.style.height = vidContainerHeight;
        if (resizeTimer.current) {
          clearTimeout(resizeTimer.current);
        }
        resizeTimer.current = setTimeout(refitToVideo, 300);
      }
    }
  }, [chaptersVisible, isMobile, vidContainerWidth, vidContainerHeight, refitToVideo]);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        background: 'linear-gradient(270deg, #1D2F44 28.99%, #071423 90.95%)',
        alignItems: 'stretch',
        justifyContent: 'center',
      }}
    >
      {!isLoaded && !tutorialStepStalled && (
        <Box
          sx={{
            display: 'flex',
            height: '100%',
            position: 'absolute',
            width: '100%',
            background: '#1D2F44',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
        >
          <CustomCircularProgress />
        </Box>
      )}
      {tutorialStepStalled && (
        <LessonDidNotLoad
          onRetry={retryTutorialStep}
          onBackToRoadmap={leaveTutorialStepForRoadmap}
        />
      )}
      <ChapterList
        sections={tutorialData.tutorial_config.sections}
        jumpToChapter={jumpToChapter}
        active={currentChapter || 0}
        lesson={tutorialData.level_number}
        visible={!isMobile || chaptersVisible}
      />
      <Box
        onClick={onVideoClick}
        sx={{
          display: 'flex',
          margin: isMobile ? 0 : '20px',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          position: 'relative',
          width: '100%',
          height: '100%',
          cursor: 'pointer',
        }}
      >
        <>
          <Box
            id="svg-processor-parent"
            ref={videoBoxRef}
            className={chaptersVisible ? 'shrunk' : 'expanded'}
            sx={{
              position: isMobile ? 'fixed' : 'relative',
              width: '100%',
              height: '100%',
              transition: '0.3s ease-in-out',
              ...(isMobile && videoContainerStylesMobile),
            }}
          >
            <ReactPlayer
              key={videoPlayerKey}
              // in order to re-pause whenever the times above are reached, the state has to stay in sync
              // with the component
              playbackRate={playbackSpeed}
              ref={videoRef}
              onPlay={playVideo}
              //controls={true}
              onSeek={onSeek}
              playing={isVideoPlaying && phaserReady && !manualPause && !videoHeld}
              type="video/mp4"
              url={TUTORIAL_VID_URL_PREFIX + tutorialData.tutorial_config.videoUrl}
              onReady={onVideoLoad}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                filter: manualPause ? PAUSED_DIM_FILTER : undefined,
              }}
              onProgress={onProgress}
              // MUS-184: events no longer depend on catching a narrow window
              // here, so this timer only has to keep chapter highlighting
              // current. Polling at 30ms added main-thread contention to a
              // thread already shared with Phaser and video decode — which is
              // what starved this timer and lost events in the first place.
              progressInterval={PROGRESS_INTERVAL_MS / playbackSpeed}
              // The video's soundtrack is the instructor's narration (MUS-87).
              // `muted` too: iOS Safari ignores a video's volume. On the iOS app
              // the volume is applyVideoNarrationGain's alone (MUS-652).
              volume={videoVolumeIgnored() ? undefined : narrationGain}
              muted={narrationGain === 0}
              // On iOS the narration volume plays through Web Audio, which needs CORS.
              config={videoPlayerConfig}
              playsinline
              width="100%"
              height="100%"
              className="react-player"
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                pointerEvents: 'none',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {
                <Box
                  sx={{
                    visibility: videoLoaded ? 'visible' : 'hidden',
                    width: videoPlayerSize.width > 0 ? videoPlayerSize.width : 1280,
                    height: videoPlayerSize.height > 0 ? videoPlayerSize.height : 720,
                    ...(isMobile && { maxWidth: '100%', maxHeight: '100%' }),
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <Box
                    data-tutorial-controls
                    // With a tap exercise on screen (playing or paused), a click
                    // here leaves focus where it was: a focused speed, volume or
                    // fullscreen button would take the next Space. Tab still
                    // reaches them from the keyboard.
                    onMouseDown={
                      tapExerciseOnScreen
                        ? (event: React.MouseEvent) => event.preventDefault()
                        : undefined
                    }
                    sx={{
                      zIndex: 99,
                      pointerEvents: 'all',
                      position: 'absolute',
                      bottom: '20px',
                      right: '20px',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    {/* While taps play, a press on the exercise is a note: pause is here. */}
                    {tapInputListening && (
                      <>
                        <TapInputPauseButton
                          onPause={() => {
                            setManualPause(true);
                            NotifyPhaserEvents.PauseExercise();
                          }}
                        />
                        <Box sx={{ marginLeft: '10px' }} />
                      </>
                    )}
                    <Box
                      sx={{
                        background: '#525969',
                        color: 'white',
                        padding: '3px 10px',
                        fontFamily: 'Lato',
                        fontWeight: 700,
                        border: '1px solid #525969',
                        ':hover': {
                          border: '1px solid white',
                          background: '#333B4D',
                        },
                      }}
                      onClick={changePlaybackSpeed}
                    >
                      <span>{`${playbackSpeed}x`}</span>
                    </Box>
                    <Box
                      sx={{
                        marginLeft: '10px',
                      }}
                    />
                    <VolumeMenuButton />
                    {/* The gap after the volume button, on every platform that shows it. */}
                    {volumeControlsAvailable() && (
                      <Box
                        sx={{
                          marginLeft: '10px',
                        }}
                      />
                    )}
                    <Fullscreen show_label={false} use_dark_icon={!manualPause} />
                  </Box>
                  <Box
                    id="tutorial-game-layer"
                    ref={gameLayerRef}
                    sx={{
                      width: '100%',
                      height: '100%',
                      visibility: showExercise ? 'visible' : 'hidden',
                      filter: manualPause ? PAUSED_DIM_FILTER : undefined,
                      // Taps play here: the layer takes the press (its overlay
                      // parent lets presses through to the video), and no
                      // panning, zoom, selection or callout may take a held
                      // finger away.
                      ...(tapInputListening && {
                        pointerEvents: 'auto',
                        touchAction: 'none',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        WebkitTouchCallout: 'none',
                      }),
                    }}
                  >
                    <PhaserGame ref={phaserRef} width={1920} height={1080} />
                  </Box>
                  {/* After the game layer, so they paint above it (the dim filter
                      makes the game layer a layer of its own), and before the
                      overlay, which stays on top and takes the tap while paused. */}
                  <Box
                    id="tutorial-step-buttons"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      filter: manualPause ? PAUSED_DIM_FILTER : undefined,
                    }}
                  >
                    <ProceedButton proceed={proceed} visibility={proceedButtonVisibility} />
                    {sceneGraded ? (
                      <SkipButton
                        skip={() => NotifyPhaserEvents.SkipExercise()}
                        visibility={skipButtonVisibility}
                      />
                    ) : (
                      <TutorialNextButton
                        visibility={skipButtonVisibility}
                        ring={nextRing}
                        ringPaused={manualPause || exercisePaused}
                        onNext={() => {
                          setNextRing(null);
                          NotifyPhaserEvents.SkipExercise();
                        }}
                        onKeepPracticing={() => {
                          setNextRing(null);
                          NotifyPhaserEvents.KeepPracticing();
                        }}
                        spacePresses={tapInputListening}
                        size={Math.max(
                          44,
                          Math.round(
                            (64 * (videoPlayerSize.height > 0 ? videoPlayerSize.height : 720)) /
                              720,
                          ),
                        )}
                      />
                    )}
                  </Box>
                  <PlayAlongIdlePause
                    playing={!sceneGraded && showExercise && !manualPause && !exercisePaused}
                    pause={() => {
                      setManualPause(true);
                      NotifyPhaserEvents.PauseExercise();
                    }}
                    resume={resumeFromPauseOverlay}
                  />
                  {manualPause && (
                    <Box
                      onClick={resumeFromPauseOverlay}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        top: 0,
                        pointerEvents: 'all',
                      }}
                    >
                      <p style={{ fontSize: 28 }}>
                        {isCapacitorNative()
                          ? 'tap here to play'
                          : 'click here, spacebar, or enter to play'}
                      </p>
                    </Box>
                  )}
                </Box>
              }
            </Box>
          </Box>
        </>
      </Box>
    </Box>
  );
};

export default InteractiveTemplate1;
